<template>
    <section
        class="max-w-3xl overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[.08] dark:bg-[#1E1D20]"
        :aria-busy="running"
        aria-live="polite"
    >
        <div
            class="flex flex-col gap-3.5 border-b border-gray-100 px-6 pb-[18px] pt-5 dark:border-white/[.06]"
        >
            <div class="flex items-start justify-between gap-4">
                <div class="flex flex-col gap-1">
                    <p
                        class="text-[15px] font-semibold text-gray-900 dark:text-white"
                    >
                        {{ headline }}
                    </p>
                    <p class="text-[13px] text-gray-500 dark:text-gray-400">
                        {{ subline }}
                    </p>
                </div>
                <div class="shrink-0 text-right">
                    <p
                        class="text-[22px] font-semibold tabular-nums tracking-tight text-gray-900 dark:text-white"
                    >
                        {{ percent }}%
                    </p>
                    <p
                        class="text-xs tabular-nums text-gray-500 dark:text-gray-400"
                    >
                        {{ clock(elapsed) }}
                    </p>
                </div>
            </div>
            <div
                class="relative h-1.5 overflow-hidden rounded-full bg-violet-50 dark:bg-white/5"
            >
                <div
                    :class="[
                        'relative h-full overflow-hidden rounded-full transition-[width] duration-300 ease-linear',
                        failed ? 'bg-rose-600' : 'bg-[#6d4cf5]',
                    ]"
                    :style="{ width: `${percent}%` }"
                >
                    <div
                        v-if="running"
                        class="absolute inset-y-0 left-0 w-2/5 animate-[loader-shimmer_1.4s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-white/55 to-transparent motion-reduce:hidden"
                    ></div>
                </div>
            </div>
        </div>

        <ol class="px-6 py-2">
            <li
                v-for="(item, index) in rows"
                :key="item.key"
                class="grid grid-cols-[22px_1fr_auto] gap-x-3.5"
            >
                <div class="flex flex-col items-center">
                    <div class="h-3.5"></div>
                    <span
                        v-if="item.state === 'done'"
                        class="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 text-[11px] font-bold text-white"
                        aria-hidden="true"
                        >✓</span
                    >
                    <span
                        v-else-if="item.state === 'active'"
                        class="h-5 w-5 animate-spin rounded-full border-2 border-violet-200 border-t-[#6d4cf5] motion-reduce:animate-none dark:border-white/10 dark:border-t-[#9b85ff]"
                        aria-hidden="true"
                    ></span>
                    <span
                        v-else-if="item.state === 'error'"
                        class="flex h-5 w-5 items-center justify-center rounded-full bg-rose-600 text-xs font-bold text-white"
                        aria-hidden="true"
                        >!</span
                    >
                    <span
                        v-else
                        class="m-0.5 h-4 w-4 rounded-full border-[1.5px] border-gray-300 dark:border-gray-600"
                        aria-hidden="true"
                    ></span>
                    <span
                        v-if="index < rows.length - 1"
                        :class="[
                            'mt-1 min-h-3 w-[1.5px] flex-1',
                            item.state === 'done'
                                ? 'bg-emerald-500'
                                : 'bg-gray-200 dark:bg-white/10',
                        ]"
                    ></span>
                </div>

                <div class="flex min-w-0 flex-col gap-1 py-3.5">
                    <p
                        :class="[
                            'text-sm',
                            item.state === 'active' || item.state === 'error'
                                ? 'font-semibold'
                                : 'font-medium',
                            item.state === 'error'
                                ? 'text-rose-700 dark:text-rose-400'
                                : item.state === 'pending'
                                  ? 'text-gray-400 dark:text-gray-500'
                                  : 'text-gray-900 dark:text-white',
                        ]"
                    >
                        {{ item.title }}
                    </p>
                    <p
                        v-if="item.detail"
                        :class="[
                            'text-[12.5px] tabular-nums',
                            item.state === 'error'
                                ? 'text-rose-700 dark:text-rose-400'
                                : item.state === 'active'
                                  ? 'text-[#6d4cf5] dark:text-[#9b85ff]'
                                  : 'text-gray-500 dark:text-gray-400',
                        ]"
                    >
                        {{ item.detail }}
                    </p>
                    <div
                        v-if="item.state === 'error'"
                        class="mt-1.5 flex gap-2"
                    >
                        <button
                            type="button"
                            :class="primaryButton"
                            @click="$emit('retry')"
                        >
                            Tentar novamente
                        </button>
                    </div>
                </div>

                <p
                    class="pt-[15px] text-xs tabular-nums text-gray-400 dark:text-gray-500"
                >
                    {{ item.right }}
                </p>
            </li>
        </ol>

        <div
            class="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 bg-gray-50 px-6 py-3 dark:border-white/[.06] dark:bg-white/[.02]"
        >
            <template v-if="running">
                <span class="text-[13px] text-gray-600 dark:text-gray-300">
                    Pode continuar usando o painel enquanto a análise roda.
                </span>
                <button
                    type="button"
                    :class="secondaryButton"
                    @click="$emit('cancel')"
                >
                    Cancelar análise
                </button>
            </template>
            <template v-else-if="cancelled">
                <span class="text-[13px] text-gray-500 dark:text-gray-400">
                    Análise cancelada.
                </span>
                <button
                    type="button"
                    :class="primaryButton"
                    @click="$emit('retry')"
                >
                    Iniciar novamente
                </button>
            </template>
            <span v-else class="text-[13px] text-rose-700 dark:text-rose-400">
                {{ error }}
            </span>
        </div>
    </section>
</template>

<script>
const STEPS = [
    { key: "prepare", title: "Preparando análise" },
    { key: "conversion", title: "Lendo as tentativas de compra" },
    { key: "approval", title: "Calculando a taxa de aprovação" },
    { key: "renewal", title: "Separando renovações de assinatura" },
    { key: "report", title: "Montando o relatório" },
];

export default {
    props: {
        step: { type: String, default: "prepare" },
        status: { type: String, default: "running" },
        startedAt: { type: Number, required: true },
        filtersLabel: { type: String, default: "" },
        error: { type: String, default: "" },
    },

    emits: ["retry", "cancel"],

    data() {
        return {
            now: Date.now(),
            stepStartedAt: this.startedAt,
            durations: {},
            timer: null,
            primaryButton:
                "h-7 rounded-md bg-[#6d4cf5] px-3 text-[13px] font-medium text-white hover:bg-[#5b3be0] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500",
            secondaryButton:
                "h-7 rounded-md border border-gray-200 bg-white px-3 text-[13px] font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700",
        };
    },

    computed: {
        running() {
            return this.status === "running";
        },

        failed() {
            return this.status === "failed";
        },

        cancelled() {
            return this.status === "cancelled";
        },

        current() {
            return Math.max(
                0,
                STEPS.findIndex((item) => item.key === this.step),
            );
        },

        elapsed() {
            return (this.now - this.startedAt) / 1000;
        },

        percent() {
            const share = 100 / STEPS.length;
            const stepSeconds = (this.now - this.stepStartedAt) / 1000;
            const partial = this.running
                ? share * (1 - Math.exp(-stepSeconds / 8))
                : 0;

            return Math.min(99, Math.floor(this.current * share + partial));
        },

        headline() {
            if (this.failed) {
                return `A análise parou na etapa ${this.current + 1} de ${STEPS.length}`;
            }

            return this.cancelled
                ? "Análise cancelada"
                : "Analisando aprovação de cartão";
        },

        subline() {
            if (this.failed) {
                return "Nenhum dado foi alterado. Tente de novo.";
            }

            if (this.cancelled) {
                return "Nenhum dado foi alterado.";
            }

            return `Etapa ${this.current + 1} de ${STEPS.length}. Períodos longos levam mais tempo.`;
        },

        rows() {
            return STEPS.map((item, index) => {
                let state = "pending";
                let detail = "";
                let right = "";

                if (index < this.current) {
                    state = "done";
                    right = this.seconds(this.durations[item.key]);
                } else if (index === this.current) {
                    state = this.failed
                        ? "error"
                        : this.running
                          ? "active"
                          : "pending";
                    right = this.running
                        ? this.seconds((this.now - this.stepStartedAt) / 1000)
                        : "";
                    detail = this.failed
                        ? "Falha nesta etapa."
                        : this.cancelled
                          ? "Interrompida"
                          : "";
                }

                if (item.key === "prepare" && state !== "error") {
                    detail = this.filtersLabel;
                }

                return { ...item, state, detail, right };
            });
        },
    },

    watch: {
        step(next, previous) {
            const at = Date.now();
            const from = STEPS.findIndex((item) => item.key === previous);
            const to = STEPS.findIndex((item) => item.key === next);
            const perStep = (at - this.stepStartedAt) / 1000 / (to - from || 1);

            STEPS.slice(Math.max(0, from), to).forEach((item) => {
                this.durations[item.key] = perStep;
            });
            this.stepStartedAt = at;
        },

        running: {
            immediate: true,
            handler(running) {
                clearInterval(this.timer);

                if (running) {
                    this.timer = setInterval(() => {
                        this.now = Date.now();
                    }, 250);
                }
            },
        },
    },

    beforeUnmount() {
        clearInterval(this.timer);
    },

    methods: {
        clock(totalSeconds) {
            const seconds = Math.max(0, Math.round(totalSeconds));

            return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
        },

        seconds(value) {
            return value === undefined
                ? ""
                : `${value.toFixed(1).replace(".", ",")}s`;
        },
    },
};
</script>

<style>
@keyframes loader-shimmer {
    0% {
        transform: translateX(-100%);
    }
    100% {
        transform: translateX(250%);
    }
}
</style>
